import { useQuery } from "@apollo/client";
import { useLocalStorage } from "@aurem/utils";
import Decimal from "decimal.js";
import { useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import type { ReactNode } from "react";
import {
  JSX,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { CurrenciesQueryQuery, gql } from "../../types";

type CurrencyContextType = {
  formatCurrency: (
    amount: Decimal | string | null | number,
    showCurrency?: boolean,
    performConversion?: boolean,
  ) => string | JSX.Element;
  formatNumberWithCurrency: (
    amount: Decimal | string | null | number,
  ) => string;
  convertToBaseCurrencyDecimal: (
    amount: Decimal | string | number,
  ) => Decimal | null;
  convertToCurrencyNumber: (amount: null | number) => number | null;
  convertToBaseCurrencyNumber: (amount: null | number) => number | null;
  formatStringWithCurrentCurrency: (str: string) => string;
  setCurrency: () => void;
  currentCurrency: string;
  currentCurrencyTranslated: string;
  currencies: CurrenciesQueryQuery["currencies"]["list"][number][];
  currenciesTranslated: (CurrenciesQueryQuery["currencies"]["list"][number] & {
    translatedId: string;
  })[];
};

type Props = {
  children: ReactNode;
};

export const CurrencyContext = createContext<CurrencyContextType>({
  formatCurrency: () => "",
  formatNumberWithCurrency: () => "",
  convertToBaseCurrencyDecimal: () => null,
  convertToCurrencyNumber: () => null,
  convertToBaseCurrencyNumber: () => null,
  formatStringWithCurrentCurrency: (str: string) => str,
  setCurrency: () => null,
  currentCurrency: "",
  currentCurrencyTranslated: "",
  currencies: [],
  currenciesTranslated: [],
});

const currenciesQuery = gql(/* GraphQL */ `
  query CurrenciesQuery {
    currencies {
      list {
        id
        isBase
        rate
      }
    }
  }
`);

const translateCurrency = (currency: string): string => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  })
    .format(0)
    .replace(/[0,.]/g, "");
};

export const CurrencyProvider = ({ children }: Props) => {
  const { authState } = useAuth();
  const { language } = useI18n();
  const { data, error } = useQuery(currenciesQuery, {
    skip: !authState?.accessToken,
  });
  const { t } = useI18n();
  const [currentCurrency, setCurrentCurrency] = useLocalStorage(
    "currency",
    process.env.BASE_CURRENCY || "AED",
  );

  const currencies = useMemo(
    () => data?.currencies?.list || [],
    [data?.currencies?.list],
  );

  const currencyMap = useMemo(
    () => new Map(currencies.map((c) => [c.id, c])),
    [currencies],
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        style: "currency",
        currency: currentCurrency as "EUR" | "USD" | "GBP" | "AED",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [language, currentCurrency],
  );

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [language],
  );

  useEffect(() => {
    if (error) {
      setCurrentCurrency(process.env.BASE_CURRENCY);
    }
  }, [error, setCurrentCurrency]);

  useEffect(() => {
    if (!currentCurrency && currencies.length) {
      const baseCurrency = currencies.find((curr) => curr.isBase);
      if (baseCurrency) {
        setCurrentCurrency(baseCurrency.id);
      }
    }
  }, [currentCurrency, currencies, setCurrentCurrency]);

  const formatCurrency = useCallback(
    (
      amount: Decimal | string | null | number,
      showCurrency = true,
      performConversion = true,
    ) => {
      const currency = currencyMap.get(currentCurrency);

      if (currency && !currency.isBase && amount && performConversion) {
        amount = new Decimal(amount).times(currency.rate).toDecimalPlaces(2);
      }

      if (showCurrency) {
        return currencyFormatter.format(Number(amount));
      }

      return numberFormatter.format(Number(amount));
    },
    [currencyMap, currentCurrency, currencyFormatter, numberFormatter],
  );

  const formatNumberWithCurrency = useCallback(
    (amount: Decimal | string | null | number) => {
      const currency = currencyMap.get(currentCurrency);
      if (currency && !currency.isBase && amount) {
        amount = new Decimal(amount).times(currency.rate).toDecimalPlaces(2);
      }
      return amount?.toString() || "-";
    },
    [currencyMap, currentCurrency],
  );

  const convertToBaseCurrencyDecimal = useCallback(
    (amount: Decimal | string | number) => {
      const currency = currencyMap.get(currentCurrency);
      if (!currency) return new Decimal(amount).toDecimalPlaces(2);
      if (currency.isBase) return new Decimal(amount);
      return new Decimal(amount).dividedBy(currency.rate).toDecimalPlaces(2);
    },
    [currencyMap, currentCurrency],
  );

  const convertToCurrencyNumber = useCallback(
    (amount: null | number) => {
      if (amount === null) return null;
      const currency = currencyMap.get(currentCurrency);
      if (currency && !currency.isBase) {
        return new Decimal(amount).times(currency.rate).toNumber();
      }
      return amount;
    },
    [currencyMap, currentCurrency],
  );

  const convertToBaseCurrencyNumber = useCallback(
    (amount: null | number) => {
      if (amount === null) return null;
      const currency = currencyMap.get(currentCurrency);
      if (currency && !currency.isBase) {
        return new Decimal(amount).dividedBy(currency.rate).toNumber();
      }
      return amount;
    },
    [currencyMap, currentCurrency],
  );

  const formatStringWithCurrentCurrency = useCallback(
    (str: string) => {
      const currencyTranslated = t(
        `apps.admin.common.currencies.${
          currentCurrency as "EUR" | "USD" | "GBP" | "AED"
        }`,
      );
      return `${str} (${currencyTranslated})`;
    },
    [currentCurrency, t],
  );

  const currenciesTranslated = useMemo(
    () =>
      currencies.map((currency) => ({
        ...currency,
        translatedId: translateCurrency(currency.id),
      })),
    [currencies],
  );

  const currentCurrencyTranslated = useMemo(
    () => translateCurrency(currentCurrency),
    [currentCurrency],
  );

  const contextValue = useMemo(
    () => ({
      formatCurrency,
      formatNumberWithCurrency,
      convertToBaseCurrencyDecimal,
      convertToCurrencyNumber,
      convertToBaseCurrencyNumber,
      formatStringWithCurrentCurrency,
      setCurrency: setCurrentCurrency,
      currentCurrency,
      currentCurrencyTranslated,
      currencies,
      currenciesTranslated,
    }),
    [
      formatCurrency,
      formatNumberWithCurrency,
      convertToBaseCurrencyDecimal,
      convertToCurrencyNumber,
      convertToBaseCurrencyNumber,
      formatStringWithCurrentCurrency,
      setCurrentCurrency,
      currentCurrency,
      currentCurrencyTranslated,
      currencies,
      currenciesTranslated,
    ],
  );

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context)
    throw new Error("useCurrency must be used within a CurrencyProvider");
  return context;
};
